import { ElMessageBox } from 'element-plus';

import dayjs from '@/plugins/dayjs';
import i18n from '@/plugins/i18n';

import { getHomeRoute } from './utils';

export const REDIRECT_REASON = {
  DEV_SUPER_ADMIN: 'devSuperAdmin',
  DEV_ONLY_ENV: 'devOnlyEnv',
  NO_ACCESS: 'noAccess',
  BILLING_HARD_GATE: 'billingHardGate',
  BILLING_TRIAL_ENDED: 'billingTrialEnded',
};

// Single source of truth for access-deny copy + redirect target. Everything is lazy:
// `redirect` because getHomeRoute() depends on runtime CASL abilities, `title`/`message` so
// t() receives a string literal (visible to extraction) resolved against the locale at show time.
const NOTICES = {
  [REDIRECT_REASON.DEV_SUPER_ADMIN]: {
    title: () => i18n.global.t('Restricted area'),
    message: () =>
      i18n.global.t("Dev Tools is available to super admins only. You'll be taken to the Dashboard."),
    redirect: (to) => ({ ...getHomeRoute(), query: to.query }),
  },
  [REDIRECT_REASON.DEV_ONLY_ENV]: {
    title: () => i18n.global.t('Not available here'),
    message: () =>
      i18n.global.t(
        "This page is only available in the development environment. You'll be taken to the Dashboard.",
      ),
    redirect: (to) => ({ ...getHomeRoute(), query: to.query }),
  },
  [REDIRECT_REASON.NO_ACCESS]: {
    // Must stay `dashboard` rather than getHomeRoute() — the guard's `to.name !== 'dashboard'`
    // check is what stops a zero-ability user from looping on the notice.
    title: () => i18n.global.t('No access'),
    message: () =>
      i18n.global.t("You don't have permission to view that page. You'll be taken to the Dashboard."),
    redirect: () => ({ name: 'dashboard' }),
  },
  [REDIRECT_REASON.BILLING_HARD_GATE]: {
    // Billing hard gate: grace period expired with no subscription. Every
    // protected route is redirected to /billing — which is allowlisted in the
    // guard, so the notice's own target never re-triggers the guard (no loop).
    title: () => i18n.global.t('Subscription required'),
    message: () =>
      i18n.global.t('Your trial and grace period have ended. Subscribe to restore access to your account.'),
    redirect: (to) => ({ path: '/billing', query: to.query }),
  },
  [REDIRECT_REASON.BILLING_TRIAL_ENDED]: {
    // Trial ended, grace period running (soft_gate + gateReason 'trial') — the
    // same full-page block as the hard gate, but the copy leads with the grace
    // deadline and the promo the user forfeits by waiting. The billing store is
    // imported lazily inside message(): a top-level import would cycle
    // (router → notices → stores/billing → stores/account → router). When the
    // store isn't initialized yet (initial deep link), gracePeriodEnd/discount
    // are null and the copy degrades to the dateless / no-promo variants.
    title: () => i18n.global.t('Your trial has ended'),
    message: async () => {
      const { useBillingStore } = await import('@/stores/billing.js');
      const billingStore = useBillingStore();
      const end = billingStore.gracePeriodEnd;
      const d = billingStore.discount;
      // Promo wording only when /status actually carries the forever percent
      // promo — never threaten forfeiting a discount the account doesn't have.
      const percent =
        d?.discount_type === 'percent_off' && d?.discount_duration === 'forever'
          ? d.discount_value
          : null;
      if (end) {
        const date = dayjs(end).format('MMMM D, YYYY');
        if (percent != null) {
          return i18n.global.t(
            "Access is paused. Subscribe by {date} to keep your {percent}% lifetime discount — after the deadline it's gone for good.",
            { date, percent },
          );
        }
        return i18n.global.t('Access is paused. Subscribe by {date} to restore access to your account.', { date });
      }
      if (percent != null) {
        return i18n.global.t(
          'Access is paused. Subscribe to restore access and keep your {percent}% lifetime discount.',
          { percent },
        );
      }
      return i18n.global.t('Access is paused. Subscribe to restore access to your account.');
    },
    redirect: (to) => ({ path: '/billing', query: to.query }),
  },
};

// Guards against stacking modals when rapid navigations re-run the guard.
let noticeOpen = false;

async function showNotice(notice) {
  if (noticeOpen) return;

  noticeOpen = true;
  try {
    // message() may be async (BILLING_TRIAL_ENDED lazily imports the billing
    // store); await handles the sync ones transparently.
    const message = await notice.message();
    await ElMessageBox.alert(message, notice.title(), {
      confirmButtonText: i18n.global.t('OK'),
      type: 'warning',
      showClose: false,
    });
  } catch {
    // Defensive: alert can't be dismissed without OK under current options, but a rejection
    // must not kill the pending navigation in blocking mode.
  } finally {
    noticeOpen = false;
  }
}

// Explain, then redirect. Set `blocking: false` on the initial navigation (deep link / reload):
// there is no page to hold back, and Preloader (z-index 9999999) would cover the alert until the
// route resolves — which never happens while the guard awaits it. Then we redirect first and let
// the notice land on top of the destination.
export function redirectWithNotice(reason, to, { blocking = true } = {}) {
  const notice = NOTICES[reason];
  const target = notice.redirect(to);
  const dismissed = showNotice(notice);

  return blocking ? dismissed.then(() => target) : target;
}
